import {useCallback} from "react";

import {IntegerField} from "@khanacademy/event-schemas";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {
    clearNotificationVariables,
    getNotificationVariable,
} from "@khan/guide-core-helpers";
import {GuideStateApi, useGuideState} from "@khan/guide-state";
import {
    ImageSource,
    type AIGuideMetadata,
    type ChatInputMetadata,
    type ImageInput,
    type Message,
    type SendGuideItemOptions,
} from "@khan/guide-types";

import {useSendGuideItem} from "../internal/use-send-guide-item.ts";
import {useStopGuideResponse} from "../internal/use-stop-guide-response.ts";
import {getLastIncomingMessageById} from "../utils/get-last-incoming-message-by-id.ts";
import {getLastOutgoingMessageById} from "../utils/get-last-outgoing-message-by-id.ts";
import parseFormattedText from "../utils/parse-formatted-text.ts";

export interface SendChatMessageFn {
    /**
     * Send a chat message to the ai guide.
     *
     * @param text The text of the message to send.
     * @param metadata The metadata for the message.
     * @param attachedImages The images attached to the message.
     * @param [options] Optional parameters for sending the message.
     * @returns A promise that resolves when the message has been sent and a
     * response has been received.
     */
    (
        text: string,
        metadata: ChatInputMetadata,
        attachedImages?: Array<ImageInput>,
        options?: SendGuideItemOptions,
    ): Promise<void>;
}

/**
 * Determines the current images to send to the AI guide.
 * Returns undefined if there's nothing for the current interaction.
 *
 * Prefer the newly attached image if one is provided.
 * Otherwise, if the last sent image was NOT moderated, send it again.
 *
 * NOTE: We currently ONLY send one image at a time, even if multiple
 * images are attached.  If we change this, we'll need to update some places in
 * the codebase:
 * 1. This function to select the images to send.
 * 2. The publish of the `AiGuideUserRequestSubmitted` event to update the
 *   `uploadedFileType` field to be an array.  This requires a schema update.
 * 3. The code in the response handler where we update the interactionID on the
 *    images when the ID is returned from the server.  This could be abstracted
 *    to a helper function.
 * 4. The code in MessageBubble that decides what thumbnail(s) to render for a
 *    message, including the helper function that create the images map.
 * 5. The code in the ImageUploadModal that currently only allows attaching a
 *    single image.
 */
function determineImageInputsToSend(
    uploadedImages: Array<ImageInput>,
    attachedImages?: Array<ImageInput>,
): Array<ImageInput> | null {
    const lastImageUploaded = uploadedImages.at(-1);

    // Case: caller passed in a new attached image, send that.
    if (attachedImages?.length) {
        return [attachedImages[0]];
    }
    // At this point, we believe that the last image uploaded
    // was already sent to the AI Guide, and we need to decide if we should
    // continue sending it.

    // Case: last image sent was moderated, don't send it.
    if (lastImageUploaded?.moderated) {
        return null;
    }
    // Case: no new image, use the last image sent, if any.
    return lastImageUploaded ? [lastImageUploaded] : null;
}

function deriveUploadedFileType(image: ImageInput | undefined) {
    switch (image?.source) {
        case ImageSource.ProvidedByFile:
            return "IMAGE_DATA_URL";
        case ImageSource.ProvidedByURL:
            return "EXTERNAL_IMAGE_URL";
        case ImageSource.ProvidedByCamera:
            return "CAMERA_CAPTURE";
        default:
            return "NO_FILE_UPLOADED";
    }
}

/**
 * A hook to send chat messages to the ai guide.
 *
 * @returns A function to send chat messages to the ai guide. It takes the
 * message text, metadata, and other optional parameters.
 */
export const useSendChatMessage = (): SendChatMessageFn => {
    const sendGuideItem = useSendGuideItem();
    const stopResponse = useStopGuideResponse();
    const uploadedImages = useGuideState("images");

    const sendChatMessage = useCallback(
        async (
            text: string,
            metadata: ChatInputMetadata,
            attachedImages?: Array<ImageInput>,
            {
                customPrompt,
                promptVersionOverride,
                demoStartDate,
                demoEndDate,
                onResponseStart: onResponseStartOverride,
                onUserPerceivedResponseStart: onUserPerceivedResponseStart,
                onResponseComplete: onResponseCompleteOverride,
                onResponseFailed,
            }: SendGuideItemOptions = {},
        ) => {
            GuideStateApi.set("isLoading", true);

            // Temporarily add the message to the conversation
            // This will be replaced by the response from the server
            const temporaryMessage: Message = {
                type: "outgoing",
                text: text,
                user: "Student",
                uiOnly: true,
                serverOnly: false,
                interactionId: "",
                attachedImage: attachedImages?.[0]?.imageURL,
            };

            const imageInputsToSend = determineImageInputsToSend(
                uploadedImages,
                attachedImages,
            );

            GuideStateApi.set("conversation", (conversation) => [
                ...conversation.filter(
                    (message) =>
                        // Filter out any outgoing messages that did not
                        // successfully get a response. This already
                        // happens when we replace the entire conversation
                        // by the response from the server but we do it at
                        // this step as well so multiple failing outgoing
                        // messages don't get added into the conversation.
                        !(message.type === "outgoing" && message.isError),
                ),
                temporaryMessage,
            ]);

            const item = {
                message: text,
                actionBubble: metadata.prepopulatedText,
                imageInputs: imageInputsToSend ?? [],
            };
            const submitTime = Date.now();
            let timeToInitialResponse = Infinity;
            let timeToInitialUserPerceivedResponse = Infinity;
            let currentInteractionId = "";
            try {
                // Called when the very first response from the server is
                // received. Used to capture the time for that response
                // to start streaming.
                const onResponseStart = () => {
                    timeToInitialResponse = Date.now() - submitTime;

                    onResponseStartOverride?.();
                };

                const onUserPerceivedResponseStart = () => {
                    timeToInitialUserPerceivedResponse =
                        Date.now() - submitTime;
                };
                // Called when the response from the server is completed
                // successfully. Used to record an analytics event at that time.
                const onResponseComplete = (
                    responseMetadata: AIGuideMetadata,
                    finalAnswer: string,
                    containsAudio: boolean,
                ) => {
                    // Capture the interactionId that was created so we can
                    // populate data for deferred analytic events.
                    currentInteractionId = responseMetadata.interactionId;

                    const {conversation, threadId} = responseMetadata;
                    const timeToFullResponse = Date.now() - submitTime;
                    // timeToInitialResponse  and timeToInitialUserPerceivedResponse should have
                    // been set, but in case they weren't let's make sure they have a sensible value.
                    timeToInitialResponse =
                        timeToInitialResponse === Infinity
                            ? timeToFullResponse
                            : timeToInitialResponse;
                    timeToInitialUserPerceivedResponse =
                        timeToInitialUserPerceivedResponse === Infinity
                            ? timeToFullResponse
                            : timeToInitialUserPerceivedResponse;

                    const outgoingMessage = getLastOutgoingMessageById(
                        conversation ?? [],
                        currentInteractionId,
                    );

                    // When an image is newly attached, it doesn't have an
                    // interactionID.
                    // The service provides the interactionID in the
                    // response, and we update the guide state.
                    if (
                        attachedImages?.length &&
                        !attachedImages[0].interactionID
                    ) {
                        // Clear the transient `isNewInput` flag once the
                        // image has been sent to the service, and before
                        // storing it in local state.
                        delete attachedImages[0].isNewInput;
                        GuideStateApi.set("images", [
                            ...GuideStateApi.latest().images,
                            {
                                ...attachedImages[0],
                                interactionID: currentInteractionId,
                                moderated:
                                    outgoingMessage?.contentFlagged ?? false,
                            },
                        ]);
                    }

                    const numContextualLinksInIncomingMessage =
                        parseFormattedText(finalAnswer.trim()).filter(
                            (c) => c.type === "link",
                        ).length;

                    const incomingMessage = getLastIncomingMessageById(
                        conversation ?? [],
                        currentInteractionId,
                    );

                    const numRecommendedRelatedLinks =
                        incomingMessage?.links?.length ?? 0;

                    const notificationID =
                        getNotificationVariable("notificationID");

                    publishAnalyticsEventForMinorConversion({
                        eventType: "AiGuideBotResponseReceived",
                        eventSchemaVersion: 7,
                        aiGuideThreadId: threadId,
                        aiGuideNavigationId:
                            GuideStateApi.latest().navigation.id,
                        aiGuideInteractionId: currentInteractionId,
                        aiGuideNotificationId: notificationID ?? "",
                        isResponseStreamed:
                            !GuideStateApi.latest().bufferResponse,
                        numSecondsBetweenRequestAndInitialResponse:
                            timeToInitialResponse * 0.001,
                        numSecondsToUserPerceivedInitialResponse:
                            timeToInitialUserPerceivedResponse * 0.001,
                        numSecondsBetweenRequestAndFullResponse:
                            timeToFullResponse * 0.001,
                        numContextualLinksIncluded: new IntegerField(
                            numContextualLinksInIncomingMessage,
                        ),
                        numRecommendedRelatedLinks: new IntegerField(
                            numRecommendedRelatedLinks,
                        ),
                        textToSpeechVoice: containsAudio
                            ? metadata.guideVoiceDescriptor
                            : null,
                        responseContainsTextToSpeechAudio: containsAudio,
                    });
                    // After the response comes in, clear the notification
                    // variables.
                    if (notificationID) {
                        clearNotificationVariables();
                    }

                    onResponseCompleteOverride?.(
                        responseMetadata,
                        finalAnswer,
                        containsAudio,
                    );
                };

                // Before sending a new message, call stopResponse()
                // to stop any active events (e.g. DAB generation)
                await stopResponse();

                await sendGuideItem(
                    "chat_message", // TODO: remove this when RPC calls are all gone
                    item,
                    {
                        customPrompt,
                        promptVersionOverride,
                        demoStartDate,
                        demoEndDate,
                        onResponseStart,
                        onUserPerceivedResponseStart,
                        onResponseComplete,
                        onResponseFailed,
                    },
                );
            } finally {
                GuideStateApi.set("isLoading", false);

                const uploadedFileType = deriveUploadedFileType(
                    attachedImages?.[0],
                );

                // We defer the AiGuideUserRequestSubmitted event in order to
                // populate the event with the interaction ID that is returned
                // from the server. If we encounter any errors or if the
                // request is aborted, we send a null aiGuideInteractionId.
                publishAnalyticsEventForMinorConversion(
                    {
                        eventType: "AiGuideUserRequestSubmitted",
                        eventSchemaVersion: 14,
                        aiGuideThreadId: GuideStateApi.latest().threadId ?? "",
                        aiGuideNavigationId:
                            GuideStateApi.latest().navigation.id,
                        aiGuideInteractionId: currentInteractionId,
                        requestUIType: metadata.requestUIType,
                        prepopulatedText: metadata.prepopulatedText,
                        numSecondsBetweenRequestTypingStartAndSubmit:
                            metadata.timeToSubmit * 0.001,
                        assessmentItemId:
                            GuideStateApi.latest().navigation
                                .assessmentItemId ?? null,
                        requestContainsSpeechToTextContent:
                            metadata.containsSpeechToTextContent,
                        uploadedFileType,
                    },
                    undefined,
                    submitTime,
                );
            }
        },
        [sendGuideItem, stopResponse, uploadedImages],
    );
    return sendChatMessage;
};
